import { useState, type FormEvent } from 'react';

import {
  PLAN_TYPES,
  type Plan,
  type PlanCreateInput,
  type PlanType,
} from '@domain/plan/Plan';
import {
  InvalidPlanInput,
  createPlan,
  updatePlan,
} from '@application/plan/managePlan';
import { apiClient } from '@infrastructure/api/client';
import { errorText } from '@infrastructure/api/errors';
import { HttpPlanRepository } from '@infrastructure/api/HttpPlanRepository';

import { Modal } from './Modal';

const repo = new HttpPlanRepository(apiClient);

interface Props {
  open: boolean;
  initial?: Plan; // present → edit mode
  onClose: () => void;
  onSaved: (plan: Plan) => void;
}

export function PlanFormModal({ open, initial, onClose, onSaved }: Props) {
  const [name, setName] = useState(initial?.name ?? '');
  const [planType, setPlanType] = useState<PlanType>(
    (initial?.plan_type as PlanType) ?? 'starter',
  );
  const [baseRate, setBaseRate] = useState(initial?.base_rate ?? '0.01');
  const [monthlyFee, setMonthlyFee] = useState(initial?.monthly_fee ?? '0.00');
  const [includedMessages, setIncludedMessages] = useState(
    String(initial?.included_messages ?? 0),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const input: PlanCreateInput = {
      name: name.trim(),
      plan_type: planType,
      base_rate: baseRate,
      monthly_fee: monthlyFee,
      included_messages: Number(includedMessages),
    };
    try {
      const saved = initial
        ? await updatePlan(repo)(initial.id, input)
        : await createPlan(repo)(input);
      onSaved(saved);
      onClose();
    } catch (e) {
      const msg = e instanceof InvalidPlanInput
        ? e.message
        : errorText(e) || 'Save failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={initial ? 'Edit Plan' : 'Create Plan'}
      onClose={onClose}
    >
      <form onSubmit={onSubmit} className="space-y-3 text-sm">
        <label className="block">
          Name
          <input
            type="text" required value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1"
          />
        </label>
        <label className="block">
          Plan type
          <select
            value={planType}
            onChange={(e) => setPlanType(e.target.value as PlanType)}
            className="mt-1 w-full rounded border px-2 py-1"
          >
            {PLAN_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            Base rate ($/SMS)
            <input
              type="number" step="0.0001" min="0.0001" max="1" required
              value={baseRate} onChange={(e) => setBaseRate(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1"
            />
          </label>
          <label className="block">
            Monthly fee ($)
            <input
              type="number" step="0.01" min="0" required
              value={monthlyFee} onChange={(e) => setMonthlyFee(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1"
            />
          </label>
        </div>
        <label className="block">
          Free texts (one-time, per account)
          <input
            type="number" min="0" required
            value={includedMessages}
            onChange={(e) => setIncludedMessages(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1"
          />
        </label>
        {initial && (initial.customer_count ?? 0) > 0 && (
          <p className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Changes apply at once to the {initial.customer_count} customer(s) on this plan.
          </p>
        )}
        {error && <p className="text-red-700">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded border px-3 py-1">
            Cancel
          </button>
          <button
            type="submit" disabled={submitting}
            className="rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50"
          >
            {submitting ? 'Saving…' : initial ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
